import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import {checkThePastDate, getFormattedDate} from '../../../../Utils/DateUtils';
import {useActions} from '../../../../Hooks/redux/useActions';
import {getDataFromStorage, setDataToStorage} from '../../../../Utils/localStorageHelpers';
import {
  LS_CREATE_ORDER_APP,
  LS_CREATE_ORDER_ATTRIBUTES,
  LS_CREATE_ORDER_COUNTRIES,
  LS_CREATE_ORDER_DATE_START,
  LS_CREATE_ORDER_DATE_STOP, LS_CREATE_ORDER_DELAY_ATTRIBUTE,
  LS_CREATE_ORDER_DELAY_TIME,
  LS_CREATE_ORDER_TYPE,
  LS_SHADOW_INSTALLS_BP_COUNTRIES,
  LS_SHADOW_INSTALLS_COUNTRIES,
  LS_SHADOW_RATES_COUNTRIES
} from '../../Constants/NewCampaignLSConstants';
import {ICountryModel} from '../../Models/CountryModel';
import CreateCampaignFormPresenter from '../../Presentor';
import {Modal} from '@mui/material';
import ImportOrderModule from '../ImportOrderModule';
import {useAppSelector} from '../../../../Hooks/redux/useAppSelector';
import {checkDelayDateIsExist} from '../../Helpers/CreateOrderHelpers';

interface ICreateBaseOrderModuleProps {
  handleCreateNewOrder: () => void;
}

const CreateBaseOrderModule = React.forwardRef(({handleCreateNewOrder}: ICreateBaseOrderModuleProps, ref) => {
  const [openImportModal, setOpenImportModal] = useState(false);

  const {countriesList, delayedStartValue} = useAppSelector(state => state.newOrderSlice);

  const internalRef = useRef();

  const {
    setOrderDateStart,
    setOrderDateStop,
    setOrderType,
    setOrderApp,
    setOrderAttributes,
    setOrderDelayedStart,
    setCountriesList,
    clearAllStateFields,
    setActiveScenario,
    setDelayedStartEnable
  } = useActions();

  const fetchDataFromStorage = () => {
    const type = getDataFromStorage(LS_CREATE_ORDER_TYPE);
    !!type && setOrderType(type);

    const dateStart = getDataFromStorage(LS_CREATE_ORDER_DATE_START);
    !!dateStart && checkThePastDate(dateStart) && setOrderDateStart(dateStart);

    const dateStop = getDataFromStorage(LS_CREATE_ORDER_DATE_STOP);
    !!dateStop && checkThePastDate(dateStop) && setOrderDateStop(dateStop);

    const application = getDataFromStorage(LS_CREATE_ORDER_APP);
    !!application && setOrderApp(Number(application));

    const attributes = getDataFromStorage(LS_CREATE_ORDER_ATTRIBUTES);
    !!attributes && setOrderAttributes(Number(attributes));

    const delayAttribute = getDataFromStorage(LS_CREATE_ORDER_DELAY_ATTRIBUTE);
    delayAttribute && setDelayedStartEnable(true);

    const delayTime = getDataFromStorage(LS_CREATE_ORDER_DELAY_TIME);

    if (delayAttribute && delayTime && dateStart) {
      if (!checkDelayDateIsExist(delayTime, dateStart)) {
        setOrderDelayedStart(delayTime);
      } else {
        localStorage.removeItem(LS_CREATE_ORDER_DELAY_TIME);
      }
    }

    const countries = localStorage.getItem(LS_CREATE_ORDER_COUNTRIES);
    !!countries && !!dateStart && checkThePastDate(dateStart) && !!dateStop && checkThePastDate(dateStop) && setCountriesList(JSON.parse(countries));
  };

  useEffect(() => {
    setActiveScenario('new_order');
    fetchDataFromStorage();

    return () => {
      clearAllStateFields();
    };
  }, []);

  const handleSetOrderType = (newType: string | null, prevType: string | null) => {
    setOrderType(newType);
    //todo не учтена дата
    const typeKeys: { [key: string]: string } = {
      rate: LS_SHADOW_RATES_COUNTRIES,
      install: LS_SHADOW_INSTALLS_COUNTRIES,
      'install_bp': LS_SHADOW_INSTALLS_BP_COUNTRIES,
    };

    if (prevType && countriesList.length > 0) {
      const key = typeKeys[prevType];
      if (key) {
        setDataToStorage(key, countriesList);
        setCountriesList([]);
        handleSetCountriesListInStorage([]);
      }
    }

    if (newType) {
      setDataToStorage(LS_CREATE_ORDER_TYPE, newType);

      const key = typeKeys[newType];
      if (key) {
        const shadowCountries = localStorage.getItem(key);
        if (shadowCountries) {
          const parsedCountries = JSON.parse(shadowCountries);
          setCountriesList(parsedCountries);
          handleSetCountriesListInStorage(parsedCountries);
        }
      }
    } else {
      Object.values(typeKeys).forEach(key => localStorage.removeItem(key));
      localStorage.removeItem(LS_CREATE_ORDER_TYPE);
    }
  };

  const handleSetOrderApp = (app: number) => {
    setOrderApp(app);
    if (app) {
      setDataToStorage(LS_CREATE_ORDER_APP, app);
    } else {
      localStorage.removeItem(LS_CREATE_ORDER_APP);
    }
  };

  const handleSetDateStart = (date: Date | null) => {
    setOrderDateStart(date ? getFormattedDate(date) : null);
    if (date) {
      if (delayedStartValue) {
        handleSetDelayStartValue(null);
      }
      setDataToStorage(LS_CREATE_ORDER_DATE_START, getFormattedDate(date));
    } else {
      localStorage.removeItem(LS_CREATE_ORDER_DATE_START);
    }
  };

  const handleSetDateStop = (date: Date | null) => {
    setOrderDateStop(date ? getFormattedDate(date) : null);
    if (date) {
      setDataToStorage(LS_CREATE_ORDER_DATE_STOP, getFormattedDate(date));
    } else {
      localStorage.removeItem(LS_CREATE_ORDER_DATE_STOP);
    }
  };

  const handleSetAttributesValue = (value: number) => {
    setOrderAttributes(value);
    if (value) {
      setDataToStorage(LS_CREATE_ORDER_ATTRIBUTES, value);
    } else {
      localStorage.removeItem(LS_CREATE_ORDER_ATTRIBUTES);
    }
  };

  const handleSetDelayStartAttribute = (value: boolean) => {
    setDelayedStartEnable(value);

    if (value) {
      setDataToStorage(LS_CREATE_ORDER_DELAY_ATTRIBUTE, value);
    } else {
      localStorage.removeItem(LS_CREATE_ORDER_DELAY_ATTRIBUTE);
      handleSetDelayStartValue(null);
    }
  };

  const handleSetDelayStartValue = (dateString: string | null) => {
    setOrderDelayedStart(dateString);
    if (dateString) {
      setDataToStorage(LS_CREATE_ORDER_DELAY_TIME, dateString);
    } else {
      localStorage.removeItem(LS_CREATE_ORDER_DELAY_TIME);
    }
  };

  const handleSetCountriesListInStorage = (countriesList: ICountryModel[]) => {
    if (countriesList.length) {
      setDataToStorage(LS_CREATE_ORDER_COUNTRIES, countriesList);
    } else {
      localStorage.removeItem(LS_CREATE_ORDER_COUNTRIES);
    }
  };

  const handleCreateNewCampaign = () => {
    handleCreateNewOrder();
  };

  const handleResetAllForms = () => {
    handleSetOrderType(null, null);
    handleSetOrderApp(0);
    handleSetAttributesValue(0);
    handleSetDelayStartValue(null);
    handleSetDelayStartAttribute(false);
    handleSetDateStart(null);
    handleSetDateStop(null);
    handleSetCountriesListInStorage([]);
    setCountriesList([]);
  };

  const handleOpenImportModal = () => {
    setOpenImportModal(true);
  };

  const handleCloseImportModal = () => {
    setOpenImportModal(false);
  };

  useImperativeHandle(ref, () => ({
    childFunction: handleResetAllForms,
    internalRef: internalRef.current,
  }));


  return (
    <>
      <CreateCampaignFormPresenter
        scenario={'new_order'}
        handleResetAllForms={handleResetAllForms}
        handleCreateNewCampaign={handleCreateNewCampaign}
        handleSetDateStop={handleSetDateStop}
        handleSetDateStart={handleSetDateStart}
        handleSetAttributesValue={handleSetAttributesValue}
        handleSetCountriesListInStorage={handleSetCountriesListInStorage}
        handleSetOrderApp={handleSetOrderApp}
        handleSetOrderType={handleSetOrderType}
        handleSetDelayStartValue={handleSetDelayStartValue}
        handleOpenImportModal={handleOpenImportModal}
        handleSetDelayStartAttribute={handleSetDelayStartAttribute}
      />
      <Modal open={openImportModal} onClose={handleCloseImportModal}>
        <ImportOrderModule
          handleCloseImportModal={handleCloseImportModal}
          handleSetDateStop={handleSetDateStop}
          handleSetDateStart={handleSetDateStart}
          handleSetCountriesListInStorage={handleSetCountriesListInStorage}
          handleSetOrderApp={handleSetOrderApp}
          handleSetOrderType={handleSetOrderType}
        />
      </Modal>
    </>

  );
});

export default CreateBaseOrderModule;